import React, {
  useState,
  useReducer,
  useEffect,
  useMemo,
  useContext,
} from "react";
import FilterFieldSearchQuery from "./FilterFieldSearchQuery/FilterFieldSearchQuery";
import { useDebounce } from "react-use";
import {
  MIN_CHARS_FOR_FILTER_QUERY,
  MAX_SEARCH_RESULTS_COUNT,
  nonSelectableScopesFields,
} from "../../../../Findings/FindingsFilters/FilterUtils";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../../General/AppPreferencesProvider";
import { SkeletonLoader, TextButton } from "@SeemplicityDev/shared";
import type { FieldData } from "../../../FilterManagement/CommonFilterTypes";

export interface FilterFieldsSearchValue {
  field: string;
  values: string[];
}

export interface MenuState {
  activeField: string | null;
  sections: string[];
  meta: string[];
}

export type MenuAction = Partial<MenuState>;

interface FilterFieldsSearchProps {
  resourceName: string;
  onChange: (filterValuesArray: FilterFieldsSearchValue[]) => void;
  clearSearchValue: () => void;
  fieldOptions?: FieldData[];
}

const FilterFieldsSearch: React.FC<FilterFieldsSearchProps> = ({
  resourceName,
  onChange,
  clearSearchValue,
  fieldOptions,
}) => {
  const [selectedFieldsValues, setSelectedFieldsValues] = useState<
    Record<string, string[]>
  >({});
  const [resourceFullView, setResourceFullView] = useState<string | null>(null);
  const [debouncedValue, setDebouncedValue] = useState("");
  const [menu, setMenu] = useReducer(
    (state: MenuState, newState: MenuAction) => ({ ...state, ...newState }),
    { activeField: null, sections: [], meta: [] }
  );

  const { isAllowed } = useContext(AppPreferencesContext);
  const canCreateScope = isAllowed(userActionsEnum.CREATE_SCOPE);

  useDebounce(
    () => {
      if (resourceName?.length >= MIN_CHARS_FOR_FILTER_QUERY) {
        setDebouncedValue(resourceName);
      }
    },
    200,
    [resourceName]
  );

  useEffect(() => {
    if (!debouncedValue) {
      setMenu({ activeField: null, sections: [], meta: [] });
    }

    if (!!debouncedValue) {
      setMenu({ sections: [], meta: [] });
    }
  }, [debouncedValue]);

  const isLoading = !!menu?.meta?.length;

  const onBackClick = () => {
    setResourceFullView(null);
    setMenu({ activeField: null });
  };

  const onShowMoreClick = (resourceType: string) => {
    setResourceFullView(resourceType);
    setMenu({ activeField: resourceType });
  };

  const onCheckOption = (field: string, values: string[]) => {
    setSelectedFieldsValues((prev) => {
      if (!!values?.length) {
        return { ...prev, [field]: values };
      } else {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
    });
  };

  const getFilterFieldSearchQuery = (resource: string) => {
    return (
      <FilterFieldSearchQuery
        resourceType={resource}
        resourceName={debouncedValue}
        onShowMoreClick={(resourceType) => onShowMoreClick(resourceType)}
        resourceFullView={resourceFullView}
        resultsCount={MAX_SEARCH_RESULTS_COUNT}
        onCheckOption={(field, values) => onCheckOption(field, values)}
        selectedValues={selectedFieldsValues[resource]}
        menu={menu}
        setMenu={setMenu}
        searchableFields={permissionsSearchableFields ?? []}
        resourceLabel={fieldLabels[resource]}
      />
    );
  };

  const noResults = !!debouncedValue && !menu?.sections?.length;

  const permissionsSearchableFields = useMemo(
    () =>
      fieldOptions
        ?.filter((field) => {
          if (nonSelectableScopesFields.includes(field?.field)) {
            return false;
          }
          if (!field?.searchable) {
            return false;
          }
          // Fail closed: only an explicit `true` skips the permission check.
          return field.visible_to_all === true ? true : canCreateScope;
        })
        .map((field) => field.field),
    [fieldOptions, canCreateScope]
  );

  const fieldLabels = useMemo(
    () =>
      Object.fromEntries(
        fieldOptions
          ?.filter((field) => !!field?.display_name)
          .map((field) => [field.field, field.display_name as string]) ?? []
      ),
    [fieldOptions]
  );

  return (
    <>
      {resourceFullView ? (
        <>
          <TextButton
            wrapperClassName={"back-to-search-view"}
            data-testid={"back-to-search-view"}
            type={"button"}
            leftIcon={"seem-icon-left-arrow"}
            label={"Back"}
            buttonStyle={"textLink"}
            onClick={onBackClick}
          />
          <div className="current-resource-list">
            {getFilterFieldSearchQuery(resourceFullView)}
          </div>
        </>
      ) : (
        <div
          className="search-results-container"
          data-testid={"resource-search-result-container"}
        >
          {permissionsSearchableFields?.map((field) =>
            getFilterFieldSearchQuery(field)
          )}
        </div>
      )}
      {isLoading ? (
        <div className="filterfield-search-loaders-container">
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="filterfield-search-loader" key={index}>
              <SkeletonLoader
                height="16px"
                width="120px"
                wrapperClassName="skeleton-loader-with-margin"
              />
              <SkeletonLoader
                height="16px"
                width="180px"
                wrapperClassName="skeleton-loader-with-margin"
              />
              <SkeletonLoader
                height="16px"
                width="180px"
                wrapperClassName="skeleton-loader-with-margin"
              />
              <SkeletonLoader
                height="16px"
                width="180px"
                wrapperClassName="skeleton-loader-with-margin"
              />
            </div>
          ))}
        </div>
      ) : (
        noResults && <div className="filterfield-no-results">No results</div>
      )}
      {(!isLoading || !noResults) && !!menu?.sections?.length && (
        <div className="form-button-group">
          <TextButton
            label={"Cancel"}
            buttonStyle={"primary"}
            onClick={() => {
              clearSearchValue();
              setMenu({ activeField: null });
            }}
          />
          <TextButton
            data-testid={`filter-preview`}
            label={"Submit"}
            buttonStyle={"primary"}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              const filterValuesArray = Object.entries(
                selectedFieldsValues
              )?.map(([key, value]) => ({ field: key, values: value }));
              onChange(filterValuesArray);
              clearSearchValue();
              e.stopPropagation();
            }}
            disabled={
              !Object.entries(selectedFieldsValues)?.length ||
              !!Object.entries(selectedFieldsValues)?.find(
                ([key, values]) => !key || !values?.length
              )
            }
          />
        </div>
      )}
    </>
  );
};

export default FilterFieldsSearch;
